"use client";

import { useState } from "react";

/**
 * Phase 2d (2026-05-08): Multi-deliverable viewer tabs wrapper.
 *
 * The customer-facing renderer's auto-stamp populates BOTH
 * `cfg.deliverable_id` (first matching) AND `cfg.deliverable_ids`
 * (full list of matches in scope) when the parent VirtualSpace
 * is order- or program-level (`primary_entity_type='order'` /
 * `'program'`). When the operator-set `deliverable_ids` list has
 * 2+ entries, viewer blocks (NavVis viewer, BIM viewer, etc.)
 * wrap each scan in a tab so the customer can flip between them
 * — instead of the order-level page silently rendering only the
 * first.
 *
 * The wrapper is generic over the block-config shape: it takes
 * the block's config, the resolved scans dictionary, the target
 * deliverable types this block class accepts, and a `renderOne`
 * callback that knows how to render a single deliverable's
 * embed. The wrapper handles the tab strip + active-state +
 * label resolution.
 *
 * Behaviour:
 *   • 0 matches → render the existing block UI with the original
 *     config (lets the block's own empty state surface).
 *   • 1 match  → render `renderOne(id)` directly, no tab chrome.
 *   • 2+ matches → render a tab strip + tab panel with the
 *     active match's embed.
 *
 * Tab labels resolve in priority order:
 *   1. scans[id].space_name (most specific — "Interior" / "Roof")
 *   2. scans[id].location_name (when scans share a property)
 *   3. "Scan N" fallback so the tab is never empty.
 *
 * Operator-set deliverable_id (single, no plural list) keeps
 * working unchanged — the auto-stamp only populates
 * `deliverable_ids` when the operator left the block empty in
 * the template clone, so customer-curated single-deliverable
 * blocks render single-embed without tabs.
 *
 * Used by every single-deliverable viewer block in
 * `virtual-space-public-renderer.tsx` (NavVis, Lidar / Faro,
 * BIM, Matterport, Floor Plan, Drone Video). Point-cloud uses
 * `point_cloud_set_id` directly (not deliverable_id) so it's
 * out of scope for this wrapper — point clouds are typically
 * 1:1 with a NavVis deliverable, so multi-N there is rare and
 * can be a follow-up if customers ask.
 */
export function MultiDeliverableViewerTabs<
  Cfg extends {
    deliverable_id?: string | null;
    deliverable_ids?: string[] | null;
    scan_id?: string | null;
    scan_ids?: string[] | null;
  },
>({
  config,
  scans,
  candidateTypes,
  renderOne,
  onlyOneFallback = "render-original",
}: {
  config: Cfg;
  scans: Record<
    string,
    {
      id: string;
      deliverable_type: string;
      space_name: string | null;
      location_name: string | null;
    }
  >;
  candidateTypes: string[];
  renderOne: (deliverableId: string) => React.ReactNode;
  /**
   * What to do when the resolved list has zero matches in scope:
   *   • "render-original" — call renderOne with the cfg's own
   *     deliverable_id (or empty string), letting the block's
   *     existing empty state surface. This is the default.
   *   • "render-empty" — render nothing at all (suppress the
   *     block entirely when there's nothing to show).
   */
  onlyOneFallback?: "render-original" | "render-empty";
}) {
  // Resolve the candidate ID list. Plural overrides singular —
  // operator-set `deliverable_ids` is the canonical source when
  // present; auto-stamp populates it for composite scopes.
  const rawList = config.deliverable_ids ?? config.scan_ids ?? null;
  const idList: string[] =
    Array.isArray(rawList) && rawList.length > 0
      ? rawList.filter((id): id is string => typeof id === "string" && !!id)
      : config.deliverable_id || config.scan_id
        ? [(config.deliverable_id ?? config.scan_id) as string]
        : [];

  // Filter to deliverables that (a) resolve in scans and
  // (b) match one of the candidate types this block class
  // can render. Type filter prevents a NavVis block from
  // accidentally tabbing through a BIM model when the
  // deliverable_ids list spans multiple types (e.g. on an
  // order-level VS where the auto-stamp drops every type's IDs
  // into the same scan_list).
  const candidates = new Set(candidateTypes);
  const matches = idList
    .map((id) => scans[id])
    .filter(
      (s): s is NonNullable<typeof s> =>
        !!s && candidates.has(s.deliverable_type),
    );

  if (matches.length === 0) {
    if (onlyOneFallback === "render-empty") return null;
    return <>{renderOne((config.deliverable_id ?? config.scan_id) ?? "")}</>;
  }

  if (matches.length === 1) {
    return <>{renderOne(matches[0].id)}</>;
  }

  return <TabsImpl matches={matches} renderOne={renderOne} />;
}

function TabsImpl({
  matches,
  renderOne,
}: {
  matches: Array<{
    id: string;
    deliverable_type: string;
    space_name: string | null;
    location_name: string | null;
  }>;
  renderOne: (deliverableId: string) => React.ReactNode;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const safeIdx = Math.min(Math.max(activeIdx, 0), matches.length - 1);
  const active = matches[safeIdx]!;

  const labels = matches.map((m, i) => labelFor(m, i, matches));

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label="Multiple scans available"
        className="flex flex-wrap gap-1.5 overflow-x-auto"
      >
        {matches.map((m, idx) => {
          const selected = idx === safeIdx;
          return (
            <button
              key={m.id}
              role="tab"
              type="button"
              aria-selected={selected}
              onClick={() => setActiveIdx(idx)}
              className={`shrink-0 rounded-full border px-3 py-1 text-xs transition-colors ${
                selected
                  ? "border-foreground/30 bg-foreground/5 text-foreground font-semibold"
                  : "border-transparent bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`}
            >
              {labels[idx]}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" aria-labelledby={`tab-${active.id}`}>
        {renderOne(active.id)}
      </div>
    </div>
  );
}

/**
 * Resolve a friendly display label for a single deliverable in
 * the tab strip. Priority:
 *
 *   1. `space_name` — most specific. Operators usually name
 *      spaces like "Interior", "Exterior", "Floor 3", "Roof"
 *      which reads cleanly as a tab label.
 *   2. `location_name` — when scans share a property but lack a
 *      space name. Common for chain rollouts (e.g. "Atlanta
 *      Store").
 *   3. "Scan N" fallback. Always non-empty.
 *
 * Disambiguates duplicate labels by appending an index when
 * two scans would otherwise produce the same tab label.
 */
function labelFor(
  match: {
    id: string;
    space_name: string | null;
    location_name: string | null;
  },
  idx: number,
  all: Array<{ space_name: string | null; location_name: string | null }>,
): string {
  const base =
    (match.space_name && match.space_name.trim()) ||
    (match.location_name && match.location_name.trim()) ||
    `Scan ${idx + 1}`;
  // Disambiguate duplicates so tabs with the same name get
  // counted (e.g. two scans both labeled "Floor 1" → "Floor 1"
  // + "Floor 1 (2)").
  const sameNameBefore = all
    .slice(0, idx)
    .filter(
      (m) =>
        ((m.space_name && m.space_name.trim()) ||
          (m.location_name && m.location_name.trim()) ||
          "") === base,
    ).length;
  if (sameNameBefore > 0) {
    return `${base} (${sameNameBefore + 1})`;
  }
  return base;
}
